import { CDTSegmentType, SegmentTableDefinition } from "src/models/segment-cdt/SegmentCdtTypes";
import DateUtils from "src/utils/dateUtils";

/**
 * Display view-model for one row of the Segment Data Tables list grid.
 *
 * Kept separate from the wire type `SegmentTableDefinition` so the grid renders presentation
 * fields (formatted segment type, formatted timestamp) without leaking formatting into the
 * contract. Every field is optional-safe: the grid renders blank rather than fabricating a value
 * where the backend has none (see the FLAGs in segment-cdt-fe/specs/03-segment-tables-list-tab.md).
 */
export interface SegmentTableListRow {
    tableId: string;
    name: string;
    segmentTypeDisplay: string;
    /** From `owner` — no confirmed table-level attribute today (OQ-03b); rendered only if present. */
    owner?: string;
    /**
     * "Last Pull" has no table-level field in the contract (`last_pull_timestamp` is per-column,
     * OQ-03c). Derived from `lastUpdatedDate` for now and labelled honestly; blank when absent.
     */
    lastPullDisplay?: string;
}

/** Human labels for the segment grain. `ENTITY` surfaces as "Legal Entity" per the create UX. */
const SEGMENT_TYPE_LABELS: Record<string, string> = {
    [CDTSegmentType.COST_CENTER]: "Cost Center",
    [CDTSegmentType.ENTITY]: "Legal Entity",
    [CDTSegmentType.LOCATION]: "Location",
    [CDTSegmentType.PRODUCT_LINE]: "Product Line",
    [CDTSegmentType.CHANNEL]: "Channel",
    [CDTSegmentType.PROJECT]: "Project",
};

export function formatSegmentType(segmentType?: CDTSegmentType | string): string {
    if (segmentType == null) {
        return "";
    }
    return SEGMENT_TYPE_LABELS[segmentType] ?? String(segmentType);
}

export function toSegmentTableListRow(def: SegmentTableDefinition): SegmentTableListRow {
    return {
        tableId: def.tableId ?? "",
        name: def.tableName ?? "",
        segmentTypeDisplay: formatSegmentType(def.segmentType),
        owner: def.owner,
        lastPullDisplay: def.lastUpdatedDate != null ? DateUtils.formatTimestamp(def.lastUpdatedDate) : undefined,
    };
}
